import { Controller } from "@hotwired/stimulus"

const FILE_TYPE_ICONS = {
  "pdf": "📄",
  "doc": "📝", "docx": "📝",
  "xls": "📊", "xlsx": "📊", "csv": "📊",
  "ppt": "📎", "pptx": "📎",
  "zip": "🗜️", "rar": "🗜️", "7z": "🗜️", "tar": "🗜️", "gz": "🗜️",
  "txt": "📃", "md": "📃", "log": "📃",
  "json": "{ }", "xml": "📋", "yml": "📋", "yaml": "📋",
  "mp4": "🎬", "mov": "🎬", "avi": "🎬", "webm": "🎬",
  "mp3": "🎵", "wav": "🎵", "ogg": "🎵",
}

function getFileIcon(filename) {
  const ext = filename.split(".").pop().toLowerCase()
  return FILE_TYPE_ICONS[ext] || "📎"
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default class extends Controller {
  static targets = [
    "writeTab", "previewTab", "writeBtn", "previewBtn",
    "body", "preview",
    "fileInput", "fileCount", "filePreviewArea", "filePreviewList",
    "mentionMenu", "mentionContainer", "mentionButton",
    "formatMenu", "formatMenuButton"
  ]
  static values = {
    nothingPreview: String,
    filesSelectedOne: String,
    filesSelectedOther: String,
    mentionUsers: Array
  }

  connect() {
    this.selectedFiles = []
    this.mentionSelection = 0
    this.rememberCursor()
    this.closeMentionsOnOutsideClick = this.closeMentionsOnOutsideClick.bind(this)
    this.closeFormatMenuOnOutsideClick = this.closeFormatMenuOnOutsideClick.bind(this)
    document.addEventListener("click", this.closeMentionsOnOutsideClick)
    document.addEventListener("click", this.closeFormatMenuOnOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.closeMentionsOnOutsideClick)
    document.removeEventListener("click", this.closeFormatMenuOnOutsideClick)
  }

  showWrite() {
    this.writeTabTarget.classList.remove("hidden")
    this.previewTabTarget.classList.add("hidden")

    this.writeBtnTarget.classList.add("bg-white", "dark:bg-gray-700", "text-gray-700", "dark:text-gray-200", "shadow-sm", "border", "border-gray-200", "dark:border-gray-600")
    this.writeBtnTarget.classList.remove("text-gray-500", "dark:text-gray-400")

    this.previewBtnTarget.classList.remove("bg-white", "dark:bg-gray-700", "text-gray-700", "dark:text-gray-200", "shadow-sm", "border", "border-gray-200", "dark:border-gray-600")
    this.previewBtnTarget.classList.add("text-gray-500", "dark:text-gray-400")
  }

  showPreview() {
    this.hideMentions()
    this.hideFormatMenu()
    this.writeTabTarget.classList.add("hidden")
    this.previewTabTarget.classList.remove("hidden")

    this.previewBtnTarget.classList.add("bg-white", "dark:bg-gray-700", "text-gray-700", "dark:text-gray-200", "shadow-sm", "border", "border-gray-200", "dark:border-gray-600")
    this.previewBtnTarget.classList.remove("text-gray-500", "dark:text-gray-400")

    this.writeBtnTarget.classList.remove("bg-white", "dark:bg-gray-700", "text-gray-700", "dark:text-gray-200", "shadow-sm", "border", "border-gray-200", "dark:border-gray-600")
    this.writeBtnTarget.classList.add("text-gray-500", "dark:text-gray-400")

    const text = this.bodyTarget.value.trim()
    if (text) {
      this.renderPreview(text)
      this.previewTarget.classList.remove("italic", "text-gray-400")
    } else {
      this.previewTarget.innerHTML = `<span class="text-gray-400 italic">${this.nothingPreviewValue}</span>`
    }
  }

  insertMention() {
    if (this.hasWriteTabTarget) this.showWrite()

    const body = this.bodyTarget
    const start = this.lastCursorPosition ?? body.selectionStart
    const end = this.lastSelectionEnd ?? body.selectionEnd
    body.setRangeText("@", start, end, "end")
    body.focus()
    this.rememberCursor()
    this.hideFormatMenu()
    this.filterMentions()
  }

  insertHeading() {
    if (this.hasWriteTabTarget) this.showWrite()

    const body = this.bodyTarget
    const cursorPosition = this.lastCursorPosition ?? body.selectionStart

    body.setRangeText("### ", cursorPosition, cursorPosition, "preserve")
    const nextCursorPosition = cursorPosition + 4
    body.setSelectionRange(nextCursorPosition, nextCursorPosition)

    body.focus()
    this.rememberCursor()
    this.hideFormatMenu()
    this.hideMentions()
  }

  insertBold() {
    if (this.hasWriteTabTarget) this.showWrite()

    const body = this.bodyTarget
    const start = this.lastCursorPosition ?? body.selectionStart
    const end = this.lastSelectionEnd ?? body.selectionEnd
    const selectedText = body.value.slice(start, end)

    if (selectedText) {
      body.setRangeText(`**${selectedText}**`, start, end, "end")
      body.setSelectionRange(start + 2, end + 2)
    } else {
      body.setRangeText("****", start, end, "end")
      body.setSelectionRange(start + 2, start + 2)
    }

    body.focus()
    this.rememberCursor()
    this.hideFormatMenu()
    this.hideMentions()
  }

  insertItalic() {
    if (this.hasWriteTabTarget) this.showWrite()

    const body = this.bodyTarget
    const start = this.lastCursorPosition ?? body.selectionStart
    const end = this.lastSelectionEnd ?? body.selectionEnd
    const selectedText = body.value.slice(start, end)

    if (selectedText) {
      body.setRangeText(`_${selectedText}_`, start, end, "end")
      body.setSelectionRange(start + 1, end + 1)
    } else {
      body.setRangeText("__", start, end, "end")
      body.setSelectionRange(start + 1, start + 1)
    }

    body.focus()
    this.rememberCursor()
    this.hideFormatMenu()
    this.hideMentions()
  }

  insertCode() {
    if (this.hasWriteTabTarget) this.showWrite()

    const body = this.bodyTarget
    const start = this.lastCursorPosition ?? body.selectionStart
    const end = this.lastSelectionEnd ?? body.selectionEnd
    const selectedText = body.value.slice(start, end)

    if (selectedText) {
      body.setRangeText(`\`${selectedText}\``, start, end, "end")
      body.setSelectionRange(start + 1, end + 1)
    } else {
      body.setRangeText("``", start, end, "end")
      body.setSelectionRange(start + 1, start + 1)
    }

    body.focus()
    this.rememberCursor()
    this.hideFormatMenu()
    this.hideMentions()
  }

  insertLink() {
    if (this.hasWriteTabTarget) this.showWrite()

    const body = this.bodyTarget
    const start = this.lastCursorPosition ?? body.selectionStart
    const end = this.lastSelectionEnd ?? body.selectionEnd
    const selectedText = body.value.slice(start, end)

    if (selectedText) {
      body.setRangeText(`[${selectedText}](url)`, start, end, "end")
      const urlStart = end + 3
      body.setSelectionRange(urlStart, urlStart + 3)
    } else {
      body.setRangeText("[](url)", start, end, "end")
      body.setSelectionRange(start + 1, start + 1)
    }

    body.focus()
    this.rememberCursor()
    this.hideFormatMenu()
    this.hideMentions()
  }

  rememberCursor() {
    if (!this.hasBodyTarget) return
    this.lastCursorPosition = this.bodyTarget.selectionStart
    this.lastSelectionEnd = this.bodyTarget.selectionEnd
  }

  preserveCursor(event) {
    this.rememberCursor()
    event.preventDefault()
  }

  toggleFormatMenu(event) {
    event.preventDefault()
    if (!this.hasFormatMenuTarget) return

    const willOpen = this.formatMenuTarget.classList.contains("hidden")
    this.formatMenuTarget.classList.toggle("hidden", !willOpen)
    if (this.hasFormatMenuButtonTarget) {
      this.formatMenuButtonTarget.setAttribute("aria-expanded", String(willOpen))
    }
  }

  hideFormatMenu() {
    if (!this.hasFormatMenuTarget) return

    this.formatMenuTarget.classList.add("hidden")
    if (this.hasFormatMenuButtonTarget) {
      this.formatMenuButtonTarget.setAttribute("aria-expanded", "false")
    }
  }

  closeFormatMenuOnOutsideClick(event) {
    if (!this.hasFormatMenuTarget || this.formatMenuTarget.classList.contains("hidden")) return
    if (this.formatMenuTarget.contains(event.target)) return
    if (this.hasFormatMenuButtonTarget && this.formatMenuButtonTarget.contains(event.target)) return

    this.hideFormatMenu()
  }

  renderPreview(text) {
    this.previewTarget.replaceChildren()

    text.split("\n").forEach((line) => {
      const heading = line.match(/^###\s+(.+)$/)
      const element = document.createElement(heading ? "h3" : "p")
      element.className = heading
        ? "mb-2 text-base font-semibold text-gray-900 dark:text-gray-100"
        : "mb-2 last:mb-0"
      this.renderInlinePreview(element, heading ? heading[1] : line)
      this.previewTarget.appendChild(element)
    })
  }

  renderInlinePreview(element, text) {
    const inlinePattern = /\*\*(.+?)\*\*|(?<!\w)_(.+?)_(?!\w)|`(.+?)`|\[([^\]]*)\]\(([^)]+)\)/g
    let lastIndex = 0
    let match

    while ((match = inlinePattern.exec(text)) !== null) {
      element.appendChild(document.createTextNode(text.slice(lastIndex, match.index)))
      const isLink = match[4] !== undefined
      const linkTarget = isLink && match[5].trim().toLowerCase() === "url" ? match[4] : match[5]
      const linkUrl = isLink ? this.safePreviewUrl(linkTarget) : null
      const tagName = match[1] !== undefined ? "strong"
        : (match[2] !== undefined ? "em" : (match[3] !== undefined ? "code" : (linkUrl ? "a" : "span")))
      const formatted = document.createElement(tagName)
      if (tagName === "code") {
        formatted.className = "px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-sm font-mono"
      } else if (tagName === "a") {
        formatted.href = linkUrl
        formatted.target = "_blank"
        formatted.rel = "noopener noreferrer"
        formatted.className = "text-indigo-600 dark:text-indigo-400 hover:underline"
      }
      formatted.textContent = isLink ? (match[4] || match[5]) : (match[1] ?? match[2] ?? match[3])
      element.appendChild(formatted)
      lastIndex = inlinePattern.lastIndex
    }

    element.appendChild(document.createTextNode(text.slice(lastIndex)))
  }

  safePreviewUrl(url) {
    const trimmedUrl = url.trim()
    if (!trimmedUrl || trimmedUrl.toLowerCase() === "url") return null

    let normalizedUrl = trimmedUrl

    if (trimmedUrl.startsWith("//")) {
      normalizedUrl = `https:${trimmedUrl}`
    } else if (!/^[a-z][a-z0-9+.-]*:/i.test(trimmedUrl) && !/^[\/#?]/.test(trimmedUrl)) {
      normalizedUrl = `https://${trimmedUrl}`
    }

    try {
      const parsed = new URL(normalizedUrl, window.location.origin)
      return ["http:", "https:", "mailto:"].includes(parsed.protocol) ? normalizedUrl : null
    } catch {
      return null
    }
  }

  filterMentions() {
    const mention = this.currentMention()
    if (!mention || !this.hasMentionMenuTarget) return this.hideMentions()

    const query = mention.query.toLocaleLowerCase()
    this.matchingMentionUsers = this.mentionUsersValue.filter((user) => {
      return user.name.toLocaleLowerCase().includes(query) || user.email.toLocaleLowerCase().includes(query)
    })
    this.mentionSelection = 0
    this.renderMentionMenu()
  }

  navigateMentions(event) {
    if (!this.isMentionMenuOpen()) return

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      const step = event.key === "ArrowDown" ? 1 : -1
      this.mentionSelection = (this.mentionSelection + step + this.matchingMentionUsers.length) % this.matchingMentionUsers.length
      this.renderMentionMenu()
    } else if (event.key === "Enter" && this.matchingMentionUsers.length) {
      event.preventDefault()
      this.selectMention(this.matchingMentionUsers[this.mentionSelection])
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.hideMentions()
    }
  }

  selectMention(user) {
    const mention = this.currentMention()
    if (!mention) return

    const body = this.bodyTarget
    body.setRangeText(`@${user.name} `, mention.start, body.selectionEnd, "end")
    body.focus()
    this.hideMentions()
  }

  currentMention() {
    if (!this.hasBodyTarget) return null

    const beforeCursor = this.bodyTarget.value.slice(0, this.bodyTarget.selectionStart)
    const match = beforeCursor.match(/(^|\s)@([^\s@]*)$/)
    if (!match) return null

    return {
      start: beforeCursor.length - match[0].length + match[1].length,
      query: match[2]
    }
  }

  renderMentionMenu() {
    if (!this.matchingMentionUsers.length) return this.hideMentions()

    const menu = this.mentionMenuTarget
    menu.replaceChildren()
    this.matchingMentionUsers.forEach((user, index) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = `flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors ${index === this.mentionSelection ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300" : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"}`

      const avatar = document.createElement("span")
      avatar.className = "flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[10px] font-semibold text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300"
      avatar.textContent = user.name.slice(0, 2).toUpperCase()

      const label = document.createElement("span")
      label.className = "min-w-0"
      const name = document.createElement("span")
      name.className = "block truncate font-medium"
      name.textContent = user.name
      const email = document.createElement("span")
      email.className = "block truncate text-xs text-gray-400 dark:text-gray-500"
      email.textContent = user.email
      label.append(name, email)
      button.append(avatar, label)
      button.addEventListener("mousedown", (event) => event.preventDefault())
      button.addEventListener("click", () => this.selectMention(user))
      menu.appendChild(button)
    })
    this.positionMentionMenu()
    menu.classList.remove("hidden")
  }

  hideMentions() {
    if (!this.hasMentionMenuTarget) return

    this.mentionMenuTarget.classList.add("hidden")
    this.mentionMenuTarget.replaceChildren()
    this.matchingMentionUsers = []
    this.mentionSelection = 0
  }

  isMentionMenuOpen() {
    return this.hasMentionMenuTarget && !this.mentionMenuTarget.classList.contains("hidden")
  }

  closeMentionsOnOutsideClick(event) {
    if (!this.isMentionMenuOpen()) return
    if (this.mentionMenuTarget.contains(event.target)) return
    if (this.mentionButtonTargets.some((button) => button.contains(event.target))) return

    this.hideMentions()
  }

  positionMentionMenu() {
    const body = this.bodyTarget
    const menu = this.mentionMenuTarget
    const tab = this.mentionContainerTarget
    const caret = this.caretCoordinates(body, body.selectionStart)

    const menuWidth = Math.min(320, tab.clientWidth - 16)
    const left = Math.min(
      body.offsetLeft + caret.left,
      tab.clientWidth - menuWidth - 8
    )

    menu.style.left = `${Math.max(8, left)}px`
    menu.style.top = `${body.offsetTop + caret.top - body.scrollTop}px`
  }

  caretCoordinates(textarea, position) {
    const mirror = document.createElement("div")
    const styles = window.getComputedStyle(textarea)
    const copiedStyles = [
      "boxSizing", "width", "height", "overflowX", "overflowY",
      "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth",
      "paddingTop", "paddingRight", "paddingBottom", "paddingLeft",
      "fontStyle", "fontVariant", "fontWeight", "fontStretch", "fontSize",
      "fontFamily", "lineHeight", "letterSpacing", "textTransform", "textIndent",
      "textAlign", "wordSpacing", "tabSize"
    ]

    mirror.style.position = "absolute"
    mirror.style.visibility = "hidden"
    mirror.style.whiteSpace = "pre-wrap"
    mirror.style.wordWrap = "break-word"
    mirror.style.top = "0"
    mirror.style.left = "-9999px"
    copiedStyles.forEach((property) => { mirror.style[property] = styles[property] })

    mirror.textContent = textarea.value.slice(0, position)
    const marker = document.createElement("span")
    marker.textContent = textarea.value.slice(position) || "."
    mirror.appendChild(marker)
    document.body.appendChild(mirror)

    const lineHeight = parseFloat(styles.lineHeight)
    const fallbackLineHeight = parseFloat(styles.fontSize) * 1.5
    const coordinates = {
      left: marker.offsetLeft,
      top: marker.offsetTop + (Number.isNaN(lineHeight) ? fallbackLineHeight : lineHeight)
    }
    mirror.remove()
    return coordinates
  }

  previewFiles() {
    const newFiles = Array.from(this.fileInputTarget.files)
    this.selectedFiles.push(...newFiles)
    this._syncFileInput()
    this._renderFilePreviews()
  }

  removeFile(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this.selectedFiles.splice(index, 1)
    this._syncFileInput()
    this._renderFilePreviews()
  }

  removeExistingFile(event) {
    event.preventDefault()

    const preview = event.currentTarget.closest("[data-comment-form-existing-file]")
    const removeInput = preview.querySelector("input[name='comment[remove_file_ids][]']")
    removeInput.checked = true
    this.element.appendChild(removeInput)
    preview.remove()
  }

  _syncFileInput() {
    const dt = new DataTransfer()
    this.selectedFiles.forEach(file => dt.items.add(file))
    this.fileInputTarget.files = dt.files
  }

  _renderFilePreviews() {
    const list = this.filePreviewListTarget
    const area = this.filePreviewAreaTarget
    const count = this.selectedFiles.length;
    const template = count === 1 ? this.filesSelectedOneValue : this.filesSelectedOtherValue;

    list.innerHTML = ""

    if (this.selectedFiles.length === 0) {
      area.classList.add("hidden")
      this.fileCountTarget.textContent = ""
      return
    }

    area.classList.remove("hidden")
    this.fileCountTarget.textContent = template.replace("%{count}", count);

    this.selectedFiles.forEach((file, index) => {
      if (file.type.startsWith("image/")) {
        this._renderImagePreview(file, index, list)
      } else {
        this._renderFileCard(file, index, list)
      }
    })
  }

  _createRemoveBtn(index) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.dataset.index = index
    btn.dataset.action = "comment-form#removeFile"
    btn.className = "absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center text-xs leading-none shadow-sm transition-colors cursor-pointer"
    btn.innerHTML = "×"
    return btn
  }

  _renderImagePreview(file, index, container) {
    const reader = new FileReader()
    reader.onload = (e) => {
      const wrapper = document.createElement("div")
      wrapper.className = "relative group/thumb"

      const img = document.createElement("img")
      img.src = e.target.result
      img.className = "w-20 h-20 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
      img.alt = file.name

      const nameTag = document.createElement("span")
      nameTag.className = "block text-xs text-gray-400 dark:text-gray-500 truncate w-20 mt-0.5"
      nameTag.textContent = file.name

      wrapper.appendChild(img)
      wrapper.appendChild(this._createRemoveBtn(index))
      wrapper.appendChild(nameTag)
      container.appendChild(wrapper)
    }
    reader.readAsDataURL(file)
  }

  _renderFileCard(file, index, container) {
    const wrapper = document.createElement("div")
    wrapper.className = "relative flex items-center gap-2 px-2.5 py-1.5 pr-7 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"

    const icon = document.createElement("span")
    icon.className = "text-base shrink-0"
    icon.textContent = getFileIcon(file.name)

    const info = document.createElement("div")
    info.className = "min-w-0 flex-1"

    const name = document.createElement("span")
    name.className = "block text-xs font-medium text-gray-700 dark:text-gray-300 truncate max-w-[140px]"
    name.textContent = file.name

    const size = document.createElement("span")
    size.className = "block text-xs text-gray-400 dark:text-gray-500"
    size.textContent = formatFileSize(file.size)

    info.appendChild(name)
    info.appendChild(size)
    wrapper.appendChild(icon)
    wrapper.appendChild(info)
    wrapper.appendChild(this._createRemoveBtn(index))
    container.appendChild(wrapper)
  }
}
